import React, { useState, useEffect, useRef } from "react";
import type { ButtonStyle, UpsellModalProduct } from "./types";
import { useCountdown } from "../../hooks/useCountdown";
import { pad, getButtonAnimation } from "../../utils/modalUtils";
import styles from "./UpsellModal.module.css";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveTemplate(template: string, productTitle: string): string {
  return template.replace(/\{product_name\}/g, productTitle);
}

const PlaceholderIcon: React.FC = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="m21 15-5-5L5 21" />
  </svg>
);

// ─── Props ────────────────────────────────────────────────────────────────────

export interface UpsellModalProps {
  product: UpsellModalProduct | null;
  /** Supports {product_name} placeholder. */
  titleTemplate: string;
  /** Supports {product_name} placeholder. */
  subtitleTemplate?: string;
  /** When non-empty, replaces product.title in display and template resolution. */
  productTitleOverride?: string;
  /** When non-empty, replaces product.description in display. */
  descriptionOverride?: string;
  titleColor?: string;
  priceColor?: string;
  modalBackgroundColor?: string;
  hideProductImage?: boolean;
  hidePriceRow?: boolean;
  acceptStyle: ButtonStyle;
  declineStyle: ButtonStyle;
  hideCloseIcon?: boolean;
  enableQuantitySelector?: boolean;
  hideVariantSelector?: boolean;
  countdown?: {
    label: string;
    hours: number;
    minutes: number;
    seconds: number;
  };
  onAccept: (quantity: number, variantId?: string) => void;
  onDecline: () => void;
  onClose?: () => void;
  onCountdownExpired?: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const UpsellModal: React.FC<UpsellModalProps> = ({
  product,
  titleTemplate,
  subtitleTemplate,
  productTitleOverride,
  descriptionOverride,
  titleColor,
  priceColor,
  modalBackgroundColor,
  hideProductImage = false,
  hidePriceRow = false,
  acceptStyle,
  declineStyle,
  hideCloseIcon = false,
  enableQuantitySelector = false,
  hideVariantSelector = false,
  countdown,
  onAccept,
  onDecline,
  onClose,
  onCountdownExpired,
}) => {
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);

  const variants = product?.variants ?? [];
  const hasRealVariants =
    variants.length > 1 ||
    (variants.length === 1 && variants[0].title !== "Default Title");
  const selectedVariant = hasRealVariants
    ? variants[selectedVariantIndex]
    : null;
  const displayPrice = selectedVariant?.price ?? product?.price ?? "0.00";
  // Track base (undiscounted) price per-variant so the strikethrough
  // updates when the user picks a different variant. Hidden when equal
  // to the discounted price (i.e. "none" action or zero-value discount).
  const displayBasePrice =
    selectedVariant?.basePrice ?? product?.basePrice ?? undefined;
  const showBasePrice = !!displayBasePrice && displayBasePrice !== displayPrice;
  const discountBadge = product?.discountBadge;

  const effectiveName = productTitleOverride?.trim() || product?.title || "";
  const effectiveDescription =
    descriptionOverride?.trim() || product?.description || "";

  const initialCountdownSeconds = countdown
    ? countdown.hours * 3600 + countdown.minutes * 60 + countdown.seconds
    : 0;

  const remainingSeconds = useCountdown(initialCountdownSeconds);

  const onCountdownExpiredRef = useRef(onCountdownExpired);
  useEffect(() => {
    onCountdownExpiredRef.current = onCountdownExpired;
  });

  useEffect(() => {
    if (initialCountdownSeconds > 0 && remainingSeconds === 0) {
      onCountdownExpiredRef.current?.();
    }
  }, [remainingSeconds, initialCountdownSeconds]);

  const showCountdown =
    !!countdown &&
    (countdown.label.trim() !== "" || initialCountdownSeconds > 0);

  const displayHours = Math.floor(remainingSeconds / 3600);
  const displayMinutes = Math.floor((remainingSeconds % 3600) / 60);
  const displaySeconds = remainingSeconds % 60;

  const handleAccept = () => {
    onAccept(quantity, selectedVariant?.id);
  };

  return (
    <div
      className={styles.modal}
      style={
        modalBackgroundColor ? { background: modalBackgroundColor } : undefined
      }
    >
      {/* Close button */}
      {!hideCloseIcon && (
        <div className={styles.closeRow}>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>
      )}

      <div className={hideCloseIcon ? styles.contentPaddedTop : styles.content}>
        {/* Countdown */}
        {showCountdown && (
          <div className={styles.countdown}>
            <span className={styles.countdownLabel}>
              {countdown!.label || "¡Oferta válida por:"}
            </span>
            <span className={styles.countdownTimer}>
              {pad(displayHours)}:{pad(displayMinutes)}:{pad(displaySeconds)}
            </span>
          </div>
        )}

        {/* Title */}
        <p className={styles.title} style={{ color: titleColor }}>
          {resolveTemplate(titleTemplate, effectiveName)}
        </p>

        {/* Subtitle */}
        {subtitleTemplate && subtitleTemplate.trim() !== "" && (
          <p className={styles.subtitle}>
            {resolveTemplate(subtitleTemplate, effectiveName)}
          </p>
        )}

        {/* Product image */}
        {!hideProductImage && (
          <div className={styles.imageWrapper}>
            {product?.imageUrl ? (
              <img
                src={product.imageUrl}
                alt={effectiveName}
                className={styles.image}
              />
            ) : (
              <div className={styles.imagePlaceholder}>
                <PlaceholderIcon />
              </div>
            )}
          </div>
        )}

        {/* Product name */}
        {effectiveName !== "" && (
          <p className={styles.productName}>{effectiveName}</p>
        )}

        {/* Description */}
        {effectiveDescription !== "" && (
          <p className={styles.description}>{effectiveDescription}</p>
        )}

        {/* Price / discount badge */}
        {hidePriceRow ? (
          discountBadge ? (
            <div className={styles.priceRow}>
              <span className={styles.discountBadge}>{discountBadge}</span>
            </div>
          ) : null
        ) : (
          <div className={styles.priceRow}>
            <p className={styles.price} style={{ color: priceColor }}>
              ${displayPrice}
            </p>
            {showBasePrice && (
              <span className={styles.basePrice}>${displayBasePrice}</span>
            )}
            {discountBadge && (
              <span className={styles.discountBadge}>{discountBadge}</span>
            )}
          </div>
        )}

        {/* Variant selector */}
        {!hideVariantSelector && hasRealVariants && (
          <div className={styles.variantWrapper}>
            <select
              className={styles.variantSelect}
              value={selectedVariantIndex}
              onChange={(e) => setSelectedVariantIndex(Number(e.target.value))}
            >
              {variants.map((v, i) => (
                <option key={v.id} value={i}>
                  {v.title} — ${v.price}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Quantity selector */}
        {enableQuantitySelector && (
          <div className={styles.quantityRow}>
            <button
              type="button"
              className={styles.quantityBtn}
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              aria-label="Reducir cantidad"
            >
              −
            </button>
            <span className={styles.quantityValue}>{quantity}</span>
            <button
              type="button"
              className={styles.quantityBtn}
              onClick={() => setQuantity((q) => q + 1)}
              aria-label="Aumentar cantidad"
            >
              +
            </button>
          </div>
        )}

        {/* Buttons */}
        <div className={styles.buttonsRow}>
          <button
            type="button"
            className={styles.acceptBtn}
            onClick={handleAccept}
            style={
              {
                "--upsell-accept-bg": acceptStyle.backgroundColor,
                "--upsell-accept-color": acceptStyle.textColor,
                "--upsell-accept-border-width": `${acceptStyle.borderWidth}px`,
                "--upsell-accept-border-color": acceptStyle.borderColor,
                "--upsell-accept-radius": `${acceptStyle.borderRadius}px`,
                "--upsell-accept-text-size": `${acceptStyle.textSize}px`,
                "--upsell-accept-shadow": acceptStyle.shadow
                  ? `0 ${acceptStyle.shadow}px ${acceptStyle.shadow * 2}px rgba(0,0,0,0.15)`
                  : "none",
                "--upsell-accept-animation": getButtonAnimation(
                  acceptStyle.animation
                ),
              } as React.CSSProperties
            }
          >
            {acceptStyle.text}
          </button>
          <button
            type="button"
            className={styles.declineBtn}
            onClick={onDecline}
            style={
              {
                "--upsell-decline-bg": declineStyle.backgroundColor,
                "--upsell-decline-color": declineStyle.textColor,
                "--upsell-decline-border-width": `${declineStyle.borderWidth}px`,
                "--upsell-decline-border-color": declineStyle.borderColor,
                "--upsell-decline-radius": `${declineStyle.borderRadius}px`,
                "--upsell-decline-text-size": `${declineStyle.textSize}px`,
                "--upsell-decline-shadow": declineStyle.shadow
                  ? `0 ${declineStyle.shadow}px ${declineStyle.shadow * 2}px rgba(0,0,0,0.15)`
                  : "none",
                "--upsell-decline-animation": getButtonAnimation(
                  declineStyle.animation
                ),
              } as React.CSSProperties
            }
          >
            {declineStyle.text}
          </button>
        </div>
      </div>
    </div>
  );
};
